/**
 * Санки публикации квиза и пейволла, вынесены из editorSlice.ts (WEB-2552,
 * пачка 49, заход Р2 рефакторинга; «перенос токен в токен», тела не
 * правились). Строковые имена санков (`editor/publishQuiz`,
 * `editor/publishPaywall`) прежние. extraReducers этих санков остались в
 * фасаде; фасад реэкспортирует имена. Действие причины тарифного отказа
 * берётся из `../actions` — не из фасада, иначе круговой импорт.
 */
import { createAsyncThunk } from "@reduxjs/toolkit";
import { paywallsApi, quizzesApi } from "@/api";
import { extractApiError } from "@/api/errorStatus";
import {
  PAYMENT_REQUIRED_SENTINEL,
  TIER_LIMIT_SENTINEL,
} from "../saveRejection";
import { setTierLimitReason } from "../actions";

/**
 * Свежий взгляд 06.10 (пачка 49), угол «шов», находка 3. Примета кошелька
 * («Недостаточно средств, пополните», `usePublishFlow`) — ТОЛЬКО настоящему
 * отказу по балансу: код `INSUFFICIENT_BALANCE` (сервер `billing.service.ts`)
 * и 402 без кода (прежний контракт WEB-136; сервер с WEB-1908 код ставит
 * всегда, но старый ответ не должен ронять сообщение в общее «не удалось»).
 * Раньше приметой становился любой 402, кроме тарифных, — и 402
 * `PROJECT_BLOCKED` (долг организации или заморозка из-за спорного платежа,
 * `assert-paywall-publishable.ts#assertProjectNotBlocked`) автор читал как
 * «пополните баланс» при порядке в балансе. Теперь такой отказ уходит
 * разобранным (`extractApiError`), и `usePublishFlow` показывает причину
 * через `localizeError` на языке кабинета.
 * WEB-3767: та же примета у публикации всей воронки с холста
 * (`components/funnel-canvas/useFunnelPublish.ts`) — правило одно.
 */
export function isWalletRefusal(code: string | undefined): boolean {
  return code === undefined || code === "INSUFFICIENT_BALANCE";
}

/**
 * Приёмка WEB-3767 (25.10): то же решение по разобранному отказу
 * (`extractApiError`) — «это 402 кошелька». Нужно холсту
 * (`components/funnel-canvas/useFunnelPublish.ts`): он держит окно тарифа и
 * по правилу WEB-1889 сам статус не сравнивает (сторож
 * `__tests__/tierRefusalRule.guard.test.ts`). Звать до `asTierLimitRejection`:
 * тот принимает 402 без кода за тариф.
 */
export function isWalletRefusalError(info: {
  status?: number | null;
  code?: string;
}): boolean {
  return info.status === 402 && isWalletRefusal(info.code);
}

export const publishQuiz = createAsyncThunk(
  "editor/publishQuiz",
  async (
    { projectId, quizId }: { projectId: string; quizId: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await quizzesApi.publish(projectId, quizId);
      return response.data as Record<string, unknown>;
    } catch (error: unknown) {
      const err = (error ?? {}) as {
        message?: string;
        status?: number;
        code?: string;
        /** WEB-1908: причина денежного отказа рядом с общим кодом. */
        reasonCode?: string;
      };
      if (err.status === 402) {
        // WEB-1908: причина отказа (`PAYMENT_REQUIRED_*`) не помещается в
        // строку-примету — кладём её в состояние рядом, чтобы окно тарифа
        // редактора сказало человеку, что именно упёрлось. Пишется всегда,
        // в том числе `null`, — иначе висела бы причина прошлого отказа.
        dispatch(setTierLimitReason(err.reasonCode ?? null));
        // WEB-391: tier-limit (free-tier publish cap) is a recoverable upsell —
        // distinct from the WEB-136 wallet-balance fatal path.
        // WEB-613: publish is now a FEATURE gate (code FEATURE_LOCKED); both it
        // and the TIER_LIMIT count-gate are recoverable tier upsells → PricingModal.
        if (err.code === "TIER_LIMIT" || err.code === "FEATURE_LOCKED") {
          return rejectWithValue(TIER_LIMIT_SENTINEL);
        }
        if (isWalletRefusal(err.code)) {
          return rejectWithValue(PAYMENT_REQUIRED_SENTINEL);
        }
        // Свежий взгляд 06.10: прочие денежные отказы (PROJECT_BLOCKED) —
        // разобранным отказом, текст называет причину через реестр кодов.
        return rejectWithValue(extractApiError(err));
      }
      // WEB-1955: реджект разобранным отказом — вызывающий (ThreePanelEditor)
      // и так гонит payload через extractApiError, а код гейта публикации
      // (NO_DOMAIN_BOUND и соседи) перестаёт теряться по дороге.
      return rejectWithValue(extractApiError(err));
    }
  },
);

export const publishPaywall = createAsyncThunk(
  "editor/publishPaywall",
  async (
    { projectId, paywallId }: { projectId: string; paywallId: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await paywallsApi.publish(projectId, paywallId);
      return response.data as Record<string, unknown>;
    } catch (error: unknown) {
      const err = (error ?? {}) as {
        message?: string;
        status?: number;
        code?: string;
        /** WEB-1908: причина денежного отказа рядом с общим кодом. */
        reasonCode?: string;
      };
      if (err.status === 402) {
        // WEB-1908: причина отказа (`PAYMENT_REQUIRED_*`) не помещается в
        // строку-примету — кладём её в состояние рядом, чтобы окно тарифа
        // редактора сказало человеку, что именно упёрлось. Пишется всегда,
        // в том числе `null`, — иначе висела бы причина прошлого отказа.
        dispatch(setTierLimitReason(err.reasonCode ?? null));
        // WEB-391: tier-limit (free-tier publish cap) is a recoverable upsell —
        // distinct from the WEB-136 wallet-balance fatal path.
        // WEB-613: publish is now a FEATURE gate (code FEATURE_LOCKED); both it
        // and the TIER_LIMIT count-gate are recoverable tier upsells → PricingModal.
        if (err.code === "TIER_LIMIT" || err.code === "FEATURE_LOCKED") {
          return rejectWithValue(TIER_LIMIT_SENTINEL);
        }
        if (isWalletRefusal(err.code)) {
          return rejectWithValue(PAYMENT_REQUIRED_SENTINEL);
        }
        // Свежий взгляд 06.10: прочие денежные отказы (PROJECT_BLOCKED) —
        // разобранным отказом, текст называет причину через реестр кодов.
        return rejectWithValue(extractApiError(err));
      }
      return rejectWithValue(extractApiError(err));
    }
  },
);
